import { ObjectSchema } from 'yup';
import type { FormikErrors } from 'formik';

import { EMPTY_OBJECT } from '@e-socna/empty';

import { useWizardContext } from '../contexts/WizardContext/hooks/useWizardContext.hook';

interface Result<V extends object, VS extends object> {
  readonly consolidatedInitialValues: V;
  readonly consolidatedValidate?: (
    values: V,
  ) => undefined | object | Promise<FormikErrors<V>>;
  readonly ConsolidatedValidationSchema: ObjectSchema<VS>;
}

export const useConsolidation = <V extends object, VS extends object>(): Result<
  V,
  VS
> => {
  const { currentStepId, props } = useWizardContext();

  const {
    initialValues,
    validate,
    ValidationSchema,
    steps,
    consolidationMode,
  } = props;

  const consolidatedInitialValues = steps.reduce(
    (acc, step) => ({ ...acc, ...(step.initialValues ?? EMPTY_OBJECT) }),
    initialValues ?? EMPTY_OBJECT,
  );

  const currentStepIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStepId),
  );

  const stepsToConsolidate =
    consolidationMode === 'allSteps'
      ? steps
      : steps.filter((_, index) =>
          consolidationMode === 'fromStartToCurrent'
            ? index <= currentStepIndex
            : index === currentStepIndex,
        );

  const consolidatedValidate = (values: V) => {
    const errors = stepsToConsolidate.reduce(
      (acc, step) => ({
        ...acc,
        ...(step.validate ? step.validate(values) : EMPTY_OBJECT),
      }),
      validate ? validate(values) : EMPTY_OBJECT,
    );

    return errors;
  };

  const ConsolidatedValidationSchema = stepsToConsolidate.reduce(
    (acc, step) => {
      if (step.ValidationSchema) {
        // COMBAK: These casts are a hack to make it work
        return acc.concat(step.ValidationSchema) as ObjectSchema<VS>;
      }

      return acc;
    },
    (ValidationSchema ?? new ObjectSchema()) as ObjectSchema<VS>,
  );

  return {
    consolidatedInitialValues: consolidatedInitialValues as V,
    consolidatedValidate,
    ConsolidatedValidationSchema,
  };
};
